import { getBlockingSettlementsForClient } from '@/services/settlement/SettlementService';
import { getOpenInvoicesForClient } from '@/services/billing/InvoiceService';
import type { BlockingSettlement } from '@/models/settlementTypes';
import type { BlockingInvoice } from '@/models/invoiceTypes';

/**
 * The client's settlements that block an accounting-mode change, or an empty list
 * if it's clear to proceed. Fails closed — a lookup failure rejects rather than
 * letting the change through while settlement state is unknown.
 *
 * Client-scoped, not matter-scoped: `Client.BillingInfoJson.billClient` is the sole
 * source of truth for the mode (`AccountingMode.IsClientAccounting`), so one toggle
 * changes it for every matter under the client. Asking only about the matter being
 * viewed missed a sibling's in-flight settlement entirely — and the old
 * `billingType !== 'contingency'` short-circuit made it worse, since matter A being
 * hourly says nothing about matter B. Which matters actually have a settlement is
 * the real predicate, and only the server can answer it.
 *
 * `settlementEnabled` is `me.features.settlementTab`. Without it the company has
 * no Settlement wizard at all, so there is nothing to block on — and calling the
 * endpoint anyway would let a 403/404 fail closed over a control that has always
 * worked for them.
 */
export async function getBlockingSettlements(
  clientId: string | undefined,
  settlementEnabled: boolean,
): Promise<BlockingSettlement[]> {
  if (!clientId || !settlementEnabled) {
    return [];
  }
  return getBlockingSettlementsForClient(clientId);
}

/**
 * The client's unpaid or partly paid invoices sitting in Manage Invoices, which
 * refuse a switch to Client Accounting outright. Fails closed, like the settlement
 * lookup: an unknown invoice state must not be read as "no invoices".
 *
 * Why a refusal and not a warning: the invoice is pinned in QuickBooks to the customer
 * it was written against, while the mode change moves the trust account to the other
 * one. Pay-from-Trust then resolves a liability against an invoice on a different
 * customer, which QuickBooks rejects — the firm finds out at the point of payment,
 * with no way back except recreating the invoice. A paid invoice has nothing left to
 * resolve, so `Closed` is not asked for.
 */
export async function getBlockingOpenInvoices(
  clientId: string | undefined,
): Promise<BlockingInvoice[]> {
  if (!clientId) {
    return [];
  }
  return getOpenInvoicesForClient(clientId);
}

/**
 * A safe, user-facing reason the accounting-mode change was refused. The raw
 * rejection must never reach `alerts.error`: it JSON-stringifies anything
 * without a `.body`, which both dumps the axios request config (including the
 * Authorization header) into the toast and silently drops a plain `Error`,
 * whose stringify is `{}`.
 */
export function billClientChangeErrorMessage(err: unknown): string {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 403) {
    return "You don't have permission to change this client's accounting setting.";
  }
  if (typeof status === 'number' && status >= 500) {
    return "Something went wrong on our end, so the accounting setting wasn't changed. Please try again.";
  }
  return "We couldn't check this client's invoices and settlements, so the accounting setting wasn't changed. Please try again.";
}

export interface TrustNode {
  id?: string | number;
  account?: { id?: string | number };
}

export interface TrustDisplayInput {
  billClient: boolean;
  matterTrust: TrustNode | null | undefined;
  clientTrust: TrustNode | null | undefined;
  trustEnabled: boolean;
  /** accountingService.verify()'s resolved state — false for both "not connected" and "still pending/timed out". */
  accountingConnected: boolean;
}

export interface TrustDisplayResult {
  /** The trust node driving the balance box + Evergreen section; null clears both. */
  trust: TrustNode | null;
  showFixTrustBanner: boolean;
}

/**
 * The "Fix Trust Accounting" banner's predicate (sc-46818), extracted so it's
 * unit-tested against the real function rather than re-derived from $scope on
 * every matter change.
 *
 * Two invariants that made the banner fire falsely before this:
 * - For a client-billed matter, `trust` IS `clientTrust` — so a genuinely
 *   computed scope can never have both `!trust` and a connected `clientTrust`
 *   at once. That combination only happened via a stale scope value, so it's
 *   guarded here directly with `!billClient` rather than left to coincidence.
 * - Nothing is asserted about "needs fixing" while accounting connectivity is
 *   unknown. A verify() timeout is not evidence of a broken trust connection.
 */
export function computeTrustDisplay({
  billClient,
  matterTrust,
  clientTrust,
  trustEnabled,
  accountingConnected,
}: TrustDisplayInput): TrustDisplayResult {
  if (!accountingConnected || !trustEnabled) {
    return { trust: null, showFixTrustBanner: false };
  }

  const trust = (billClient ? clientTrust : matterTrust) ?? null;
  const clientTrustConnected = !!clientTrust?.account?.id;

  return {
    trust,
    showFixTrustBanner: !billClient && !trust && clientTrustConnected,
  };
}

export interface AccountingModeDialogParams {
  oldModeIsClientAccounting: boolean;
  newModeIsClientAccounting: boolean;
  /** The client whose settlements are reset in one call if the user confirms. */
  clientId: string;
  /** Every settlement under the client that blocks the change — empty when invoices are the reason. */
  settlements: BlockingSettlement[];
  /**
   * Unpaid or partly paid invoices under the client. Non-empty means an outright
   * refusal with no offer to proceed, and `settlements` is empty — the invoice
   * check runs first and returns before the settlement lookup happens.
   */
  invoices: BlockingInvoice[];
  /** `me.terms.Matter` — firms rename "Matter", and the toggle already honours it. */
  matterTerm: string;
}

export interface BillClientChangeDeps {
  /** Opens AccountingModeChangeDialog; resolves `true` once Unlock & Reset succeeds. */
  openDialog: (params: AccountingModeDialogParams) => Promise<boolean>;
  /** Applies the accounting-mode change — accountingModeGuard.ts records the clearance and lets its caller apply it. */
  performBillClientChange: (value: boolean) => void;
}

export interface BillClientChangeContext {
  /** The mode the user just clicked — `true` is Client Accounting. */
  value: boolean;
  /**
   * The currently active mode. `undefined` when none has been set yet, which is
   * a real state: `activate()` leaves it undefined when the client has no
   * `billing.billClient` and the company has no `clientBilling` default, and the
   * template then highlights neither button.
   */
  billClientValue: boolean | undefined;
  /** The client the mode belongs to — `matter.client.id`, not the matter's own id. */
  clientId: string | undefined;
  /** `me.features.settlementTab`. */
  settlementEnabled: boolean;
  /** `me.features.settlementClientAccounting` — off, the change applies directly. */
  clientAccountingEnabled: boolean;
  /** `me.terms.Matter`. */
  matterTerm: string;
}

/**
 * The "Bill Client" toggle's decision logic, extracted out of
 * matter.billing.ctrl.js so tests exercise the real code, not a mirrored copy.
 */
export async function billClientChangeDecision(
  {
    value,
    billClientValue,
    clientId,
    settlementEnabled,
    clientAccountingEnabled,
    matterTerm,
  }: BillClientChangeContext,
  { openDialog, performBillClientChange }: BillClientChangeDeps,
): Promise<void> {
  // `undefined` is a real state (no mode set yet) and must not collapse to
  // false, or the Matter button could never be selected. Ungated on purpose.
  if (typeof billClientValue === 'boolean' && value === billClientValue) return;

  if (!clientAccountingEnabled) {
    performBillClientChange(value);
    return;
  }

  // Below the flipper on purpose, so nothing this branch does can reach a firm
  // without settlementClientAccounting — with the flag off the return above already
  // applies the change, which is what this would have done anyway.
  //
  // Writing an unset mode out as an explicit `false` still has to happen — that
  // write is what highlights the button — but it is not a *change* of accounting
  // mode, so it must not reach the settlement guard. It used to: the guard refused
  // a non-change and opened the dialog reading "from Matter Accounting to Matter
  // Accounting" (sc-47290). Strict equality above stays, so a genuine no-op click
  // still writes nothing at all.
  //
  // Ahead of the lookup, not after it: a non-change must not cost a network call
  // either, and the client-scoped query below is heavier than the matter-scoped
  // one it replaced.
  if (!!billClientValue === !!value) {
    performBillClientChange(value);
    return;
  }

  // Checked before the lookup rather than alongside its result: the reset is
  // client-scoped, so with no client there is nothing to look up and nothing the
  // dialog could act on. Also what narrows `clientId` to a plain string below.
  if (!clientId) {
    performBillClientChange(value);
    return;
  }

  // Ahead of the settlement lookup because this refusal is unconditional: there is
  // nothing to offer the user, so pricing a settlement reset they could never take
  // would spend a round trip to describe a choice that does not exist.
  //
  // Both directions. An open invoice is pinned to the QuickBooks customer it was
  // written against, and either move puts the trust account on the other one — up to
  // the client, or back down to the matter — so Pay-from-Trust ends up resolving a
  // liability against an invoice on a different customer whichever way it went. The
  // no-op guards above have already returned, so reaching here is a real change.
  const invoices = await getBlockingOpenInvoices(clientId);
  if (invoices.length > 0) {
    // Result ignored on purpose — the dialog has no confirm in this mode, so the
    // only outcome is dismissal and the change never applies.
    await openDialog({
      oldModeIsClientAccounting: billClientValue === true,
      newModeIsClientAccounting: value,
      clientId,
      settlements: [],
      invoices,
      matterTerm,
    });
    return;
  }

  const settlements = await getBlockingSettlements(clientId, settlementEnabled);
  if (settlements.length === 0) {
    performBillClientChange(value);
    return;
  }

  const didReset = await openDialog({
    oldModeIsClientAccounting: billClientValue === true,
    newModeIsClientAccounting: value,
    clientId,
    settlements,
    invoices: [],
    matterTerm,
  });
  if (didReset) {
    performBillClientChange(value);
  }
}
